import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FaTimes, FaSave } from "react-icons/fa";
import { ROLES } from "./Users";

const UserModal = ({ isOpen, onClose, onSubmit, user }) => {
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [clubId, setClubId] = useState("");

  const dialogRef = useRef(null);
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  const demoMode = userInfo?.role_id === 2;

  useEffect(() => {
    if (!isOpen) return;

    setNom(user?.nom || "");
    setPrenom(user?.prenom || "");
    setEmail(user?.email || "");
    setRole(user?.role_id || "");
    setClubId(user?.Club?.id ?? user?.club_id ?? "");

    const root = document.documentElement;
    const prev = root.style.overflow;
    root.style.overflow = "hidden";

    const onEsc = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onEsc);

    const t = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='nom']")?.focus();
    }, 0);

    return () => {
      root.style.overflow = prev;
      window.removeEventListener("keydown", onEsc);
      clearTimeout(t);
    };
  }, [isOpen, user, onClose]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (demoMode) return;
    if (!nom || !prenom || !email || !role) return;

    onSubmit({
      nom,
      prenom,
      email,
      role_id: parseInt(role, 10),
      club_id: clubId ? parseInt(clubId, 10) : null,
    });
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
      role="dialog"
      aria-modal="true"
      aria-label="Éditer l'utilisateur"
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh]"
      >
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6 flex items-center justify-between">
          <div>
            <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
              {user ? "Modifier l'utilisateur" : "Créer un utilisateur"}
            </h3>
            <p className="text-white/90 text-xs sm:text-sm">
              Mettez à jour les informations de l’utilisateur.
            </p>
          </div>
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
            aria-label="Fermer"
            type="button"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6"
        >
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Nom
              </label>
              <input
                name="nom"
                type="text"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Prénom
              </label>
              <input
                type="text"
                value={prenom}
                onChange={(e) => setPrenom(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Rôle
              </label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
                required
              >
                <option value="">Sélectionner un rôle</option>
                {ROLES.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                ID Club (optionnel)
              </label>
              <input
                type="number"
                value={clubId}
                onChange={(e) => setClubId(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
                placeholder="Ex : 12"
              />
              <p className="text-xs text-slate-500 mt-1">
                Laissez vide pour ne pas modifier.
              </p>
            </div>
          </div>

          <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 mt-6">
            <div className="pointer-events-none h-6 bg-gradient-to-t from-white/95 dark:from-slate-900/95 to-transparent" />
            <div className="pointer-events-auto bg-white/95 dark:bg-slate-900/95 border-t border-slate-200/70 dark:border-slate-800/70 px-4 sm:px-6 py-3 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl bg-slate-100 text-slate-800 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 inline-flex items-center gap-2"
              >
                <FaTimes /> Annuler
              </button>
              <button
                type="submit"
                disabled={demoMode}
                className={`px-4 py-2 rounded-xl inline-flex items-center gap-2 ${
                  demoMode
                    ? "bg-slate-400 cursor-not-allowed text-white"
                    : "bg-violet-600 hover:bg-violet-700 text-white"
                }`}
                title={demoMode ? "Mode Démo" : "Enregistrer"}
              >
                <FaSave /> Enregistrer
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default UserModal;